<template>
  <!-- Asks for an expression and build links to search on different websites -->
  <div id="mainContainer">
    <help-popup v-bind:show="showHelp" v-bind:text="pageText.help.text[selectedLang]" v-bind:title="pageText.help.title[selectedLang]" v-bind:selectedLang="selectedLang"></help-popup>
     <!-- Display title with an help button -->
    <div id="objectTitleContainer">
      <div id="helpButtonSizer">
      </div>
      <div id="objectTitleSizer">
        <h1>{{pageText.title[selectedLang]}}</h1>
      </div>
      <div id="helpButtonSizer" v-on:click="triggerHelp(true)">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-question-circle-fill" viewBox="0 0 16 16">
          <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.496 6.033h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286a.237.237 0 0 0 .241.247zm2.325 6.443c.61 0 1.029-.394 1.029-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94 0 .533.425.927 1.01.927z"/>
        </svg>
      </div>
    </div>
    <!-- Where users can choose their language and type an expression -->
    <div id="formContainer">
      <!-- Choose a language with little flags -->
      <div id="langagePicker">
        <langage-picker-vue v-bind:lang="sourceLang"></langage-picker-vue>
      </div>
      <!-- Type an expression -->
      <div id="inputContainer">
        <input class="inputContainerElement" v-on:keyup.enter="redirectToCurrentPage()" id="inputText" v-model="userWord"/>
        <button id="inputButton" class="btn btn-primary inputContainerElement" v-on:click="redirectToCurrentPage()">{{pageText.form.button[selectedLang]}}</button>
      </div>
    </div>
    <!-- Where user's request answers are shown -->
    <div id="answerContainer" v-if="showAnswer">
      <!-- Remind user what is searched -->
      <div id="answerIndication">
        <label>{{pageText.answer.label[selectedLang]}}"{{searchReminder}}"</label>
      </div>
      <!-- Display cards containing links -->
      <div id="cardsContainer">
        <div class="card" v-for="dictionaryType in dictionaries[sourceLang]" v-bind:key="dictionaryType">
            <div class="card-body">
              <h5 class="card-title">{{dictionaryType.name[selectedLang]}}</h5>
              <!-- <h6 class="card-subtitle text-muted">{{pageText.content.tip.description[selectedLang]}}</h6> -->
              <ul>
                  <li v-for="item in dictionaryType.items" v-bind:key="item"><a v-bind:href="buildUrl(item)">{{item.name}}</a></li>
              </ul>
            </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LangagePickerVue from './LangagePicker.vue'
import HelpPopup from './HelpPopup.vue'

export default {
  name: 'Dictionaries',
  components : {
    LangagePickerVue,
    HelpPopup
  },
  props : {
    selectedLang : String
  },
  methods : {
    /** 
     * Function triggered when a language is picked
     * 
     * @param newLang : A string containing one of these three elements : 'en', 'nl' or 'fr'
     * 
     * */ 
    changeLang(newLang){
      this.sourceLang = newLang;
    },

    /**
     * Reload current page with different parameters
     */
    redirectToCurrentPage(){
      this.$router.push({name : 'Dictionaries', params : {
          lang : this.sourceLang,
          word : this.userWord
        }})
      this.handleInput();
      this.userWord = "";
    },

    /** 
     * Function handling input parsing
     * */
    handleInput (){
      if (this.userWord != "" && this.userWord != undefined) {
        // Enable display of the answer section
        this.showAnswer = true;
        // We need a list of every word to build links
        this.wordList = this.userWord.split(" ");
        // Store researched word in a variable for later display
        this.searchReminder = this.userWord;
      }
    },

    /**
     * Based on the representation of a website, build an url
     * 
     * @param item : The representation of a website
     * @requires item must be a dictionary like {
              name : 'The name the link will show',
              baseUrl : 'The part or url before keywords',
              separator : 'chars between each keyword',
              tailUrl : 'The part of url after keywords'
            }
     */
    buildUrl(item){
      // Retreive the base of the url
      let baseUrl = item.baseUrl;
      // Build the url with each keyword separated by separator
      for (const searchTerm in this.wordList) {
        baseUrl += this.wordList[searchTerm] + item.separator;
      }
      // Remove the exceeding separator and add the url tail
      return baseUrl.slice(0, - item.separator.length) + item.tailUrl
    },
    /**
    * Set showHelp to `show`
    * 
    * @param show (boolean) : Value to be set for showHelp
    */
    triggerHelp(show){
      this.showHelp = show;
    }
  },
  data () {
    return {
      // Static text on the page, available
      // in english, french and dutch
      pageText : {
        title : {
          en : 'Online dictionaries',
          fr : 'Dictionnaires en ligne',
          nl : 'Online Woordenboeken'
        },
        form : {
          label : {
            en : 'Word : ',
            fr : 'Mot : ',
            nl : 'Word : '
          },
          button : {
            en : 'Search',
            fr : 'Rechercher',
            nl : 'Zoeken'
          }
        },
        answer : {
          label : {
            en : "Results for ",
            fr : 'Résultats pour ',
            nl : 'Resultaten voor '
          }
        },
        help : {
          title : {
            fr : "Utilisation de l'outil dictionnaires",
            en : "Using the dictionary tool",
            nl : "Gebruik van het woordenboek"
          },
          text : {
            fr : "Met à votre disposition des liens vers des dictionnaires en ligne.",
            en : "Provides links to online dictionaries.",
            nl : "Geeft links naar online woordenboeken."
          }
        }
      },
      // For each possible language choosable in
      // the languagePicker, specifies different kinds
      // of dictionaries and thier items 
      dictionaries : {
        en : {
          normal_dictionaries : {
            name : {
              en : 'Dictionaries',
              fr : 'Dictionnaires',
              nl : 'Woordenboeken' 
            },
            items : [{
              name : 'Macmillan Dictionary',
              baseUrl : 'https://www.macmillandictionary.com/search/british/?q=',
              separator : '+',
              tailUrl : ''
            },
            {
              name : 'Howjsay',
              baseUrl : 'https://howjsay.com/how-to-pronounce-',
              separator : '+',
              tailUrl : ''
            },
            {
              name : 'Longman Dictionary of Contemporary English',
              baseUrl : 'https://www.ldoceonline.com/dictionary/',
              separator : '-',
              tailUrl : ''
            },
            {
              name : 'The Free Dictionary',
              baseUrl : 'https://www.thefreedictionary.com/',
              separator : ' ',
              tailUrl : ''
            }]
          },
          en_fr_dict : {
            name : {
              en : 'English - French Dictionaries',
              fr : 'Dictionnaires Anglais - Français',
              nl : 'Engels - Frans Woordenboeken' 
            },
            items : [
              {
                name : 'Reverso',
                baseUrl : 'https://dictionnaire.reverso.net/anglais-francais/',
                separator : '+',
                tailUrl : ''
              },
              {
                name : 'Wordreference',
                baseUrl : 'https://www.wordreference.com/enfr/',
                separator : ' ',
                tailUrl : ''
              },
            ]
          },
          en_nl_dict : {
            name : {
              en : 'English - Dutch Dictionaries',
              fr : 'Dictionnaires Anglais - Néerlandais',
              nl : 'Engels - Nederlands Woordenboeken' 
            },
            items : [
              {
                name : 'Sensagent',
                baseUrl : 'http://traduction.sensagent.com/',
                separator : ' ',
                tailUrl : '/en-nl/'
              }
            ]
          },
        },
        fr : {
          fr_en_dict : {
            name : { 
              en : 'French - English dictionaries',
              fr : 'Dictionnaires Français - Anglais',
              nl : 'Frans - Engels Woordenboeken' 
            },
            items : [
              {
                name : 'Reverso',
                baseUrl : 'https://dictionnaire.reverso.net/francais-anglais/',
                separator : '+',
                tailUrl : ''
              },
            ]
          },
        },
        nl : {
          normal_dict : {
            name : { 
              en : 'Dutch dictionaries',
              fr : 'Dictionnaires Néerlandais',
              nl : 'Nederlands Woordenboeken' 
            },
            items : [
              {
                name : 'Encyclo',
                baseUrl : 'https://www.encyclo.nl/begrip/',
                separator : '_',
                tailUrl : ''
              },
              {
                name : 'Van Dale',
                baseUrl : 'https://www.vandale.nl/gratis-woordenboek/nederlands/betekenis/',
                separator : ' ',
                tailUrl : ''
              },
            ]
          },
          nl_fr_dict : {
            name : { 
              en : 'Dutch - French dictionaries',
              fr : 'Dictionnaires Néerlandais - Français',
              nl : 'Nederlands - Frans Woordenboeken' 
            },
            items : [
              {
                name : 'Sensagent',
                baseUrl : 'http://traduction.sensagent.com/',
                separator : ' ',
                tailUrl : '/nl-fr/'
              },
            ]
          },
          nl_en_dict : {
            name : { 
              en : 'Dutch - English dictionaries',
              fr : 'Dictionnaires Néerlandais - Anglais',
              nl : 'Nederlands - Engels Woordenboeken' 
            },
            items : [
              {
                name : 'Sensagent',
                baseUrl : 'http://traduction.sensagent.com/',
                separator : ' ',
                tailUrl : '/nl-en/'
              },
            ]
          },
          synonym : {
            name : { 
              en : 'Synonyms dictionaries',
              fr : 'Dictionnaires de synonymes',
              nl : 'Synoniemen Woordenboeken' 
            },
            items : [
              {
                name : 'Synoniemen.net',
                baseUrl : 'https://synoniemen.net/index.php?zoekterm=',
                separator : ' ',
                tailUrl : ''
              },
            ]
          },
        }
    },
      // Language choosed in the languagePicker
      sourceLang : 'en',
      // What user enters in the textbox
      userWord : '',
      // A list of words in `userWord`
      wordList : [],
      // Enable/Disable showing the answer section
      showAnswer : false,
      // Show/Hide help popup
      showHelp : false,
      // Keep in memory the object of the search
      searchReminder : "",
    }
  },
  // On load function
  activated : function () {
    // load lang from route param if given value is correct
    let langOk = false;
    if (['fr', 'nl', 'en'].includes(this.$route.params.lang)) {
      this.sourceLang = this.$route.params.lang;
      langOk = true
    }
    // Load word into userword. If not provided, this.$route.params.word
    // simply returns '', which 
    this.userWord = this.$route.params.word;

    if (langOk && this.userWord != "") {
      this.handleInput()
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

#mainContainer {
  width: 100%;
  height: 97%;
}

h1 {
  text-align: center;
  font-size: 2vw;
}

#formContainer {
  width : 100%;
  height: 6rem;
  display : flex;
  flex-direction: column;
}

#langagePicker {
  width: 15%;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
}

#inputContainer{
  display: flex;
  justify-content: flex-start;
  width: 25rem;
  margin-top : auto;
  margin-left: auto;
  margin-right: auto; 
}

#inputText {
  width: 66%;
}

#inputButton {
  width: 25%;
}

#answerContainer {
  width: 100%;
  height: 50%;
}

#answerIndication {
  text-align: center;
  font-size: 150%;
}

#cardsContainer {
  margin-top : 5%;
  width: 100%;
  height: 70%;
  display: flex;
  justify-content: space-around;
}

@media screen and (max-width: 850px) {
  #mainContainer {
    height: auto;
  }

  #cardsContainer {
    flex-direction: column;
  }

  #formContainer {
    width : 100%;
    height: 40%;
  }

  .card {
    margin-top: 2vh;
  }

  #inputContainer {
    width: 90%;
  }

  #langagePicker {
    width: 40%;
  }

  #answerContainer {
    height: auto;
  }

  h1 {
    font-size: 10vw;
  }
}

@media screen and (max-width: 850px) and (orientation : landscape){
  h1 {
    font-size: 5vw;
  }

  #cardsContainer {
    flex-direction: row;
  }
}
</style>
